/**
 * Maps backend responses onto the records the admin page sources are made of. Pure and shared, so
 * the firm Overview and LAB Routing describe the same route, provider, or rule the same way.
 */
import type { BillingRule } from '@thinkalpha/locates-ws-client';
import type { ConcreteAccount } from '@thinkalpha/trading-ws-client/contracts/account.js';
import type { FirmMembershipWithProfile, FirmWithParentsAndChildren } from '@thinkalpha/ui-types/types/admin/firms';
import type { InventoryPool } from '@thinkalpha/ui-types/types/admin/lab-pools';
import type {
    AccountRecordLite,
    BillingRuleLite,
    FirmRecord,
    FirmRef,
    MemberRecord,
    PoolLite,
    ProviderLite,
    RouteLite,
} from 'src/admin/firms/FirmOverview/source';
import type { Provider } from 'src/admin/LAB/contracts/providers';
import type { Route } from 'src/admin/LAB/contracts/routes';
import { grantedFirmIds } from 'src/admin/lib/permissions';

export const toFirmRef = (ref: FirmRef): FirmRef => ({
    id: ref.id,
    name: ref.name,
    displayName: ref.displayName,
});

const isRoleName = (role: string | { deny: string }): role is string => typeof role === 'string';

export function toFirmRecord(firm: FirmWithParentsAndChildren): FirmRecord {
    return {
        id: firm.id,
        name: firm.name,
        displayName: firm.displayName,
        mpids: firm.mpids,
        tradingSystemName: firm.tradingSystemName,
        availableRoles: firm.availableRoles.filter(isRoleName),
        appliedRoles: firm.appliedRoles.filter(isRoleName),
        parents: (firm.parents ?? []).map(toFirmRef),
        children: (firm.children ?? []).map(toFirmRef),
    };
}

/** A membership is on only if it is active and the membership it depends on is, recursively. */
function isEffectivelyActive(
    membership: FirmMembershipWithProfile,
    byId: ReadonlyMap<string, FirmMembershipWithProfile>,
    seen = new Set<string>(),
): boolean {
    if (!membership.active || seen.has(membership.id)) return false;
    if (!membership.parentMembershipId) return true;
    const parent = byId.get(membership.parentMembershipId);
    // A parent we never listed leaves only the membership's own flag to go on.
    if (!parent) return true;
    seen.add(membership.id);
    return parent.userId === membership.userId && isEffectivelyActive(parent, byId, seen);
}

export function toMemberRecord(
    membership: FirmMembershipWithProfile,
    byId: ReadonlyMap<string, FirmMembershipWithProfile>,
): MemberRecord {
    const { profile } = membership;
    const parent = membership.parentMembershipId ? byId.get(membership.parentMembershipId) : undefined;
    return {
        userId: membership.userId,
        displayName: `${profile.firstName} ${profile.lastName}`.trim() || profile.username || profile.email,
        email: profile.email,
        permission: membership.permission,
        active: isEffectivelyActive(membership, byId),
        userActive: profile.active,
        dependsOnFirmId: parent?.firmId,
    };
}

export const toAccountLite = (account: ConcreteAccount): AccountRecordLite => ({
    id: account.id,
    name: account.name,
    displayName: account.displayName,
    mpid: account.mpid,
    tradingPlatform: account.tradingPlatform,
    locatesPlatform: account.locatesPlatform ?? undefined,
    status: account.status,
});

export function toProviderLite(provider: Provider, knownFirmIds: ReadonlySet<string>): ProviderLite {
    return {
        id: provider.id,
        name: provider.name,
        firmId: provider.firmId,
        enabled: provider.enabled,
        live: provider.integration?.isLive ?? false,
        supportsPreborrow: provider.features?.isPreBorrow ?? provider.isPreBorrow,
        // The backend returns the permission list to owners only, so non-owners see no firms here.
        sharedWithFirmIds: grantedFirmIds(provider, knownFirmIds),
    };
}

export function toRouteLite(route: Route, knownFirmIds: ReadonlySet<string>): RouteLite {
    return {
        id: route.id,
        name: route.name,
        privateName: route.privateName,
        firmId: route.firmId,
        timeoutSeconds: route.configuration?.timeout ?? null,
        targets: route.providers.map(({ providerId, bidPriority }) => ({ providerId, bidPriority })),
        grantedFirmIds: grantedFirmIds(route, knownFirmIds),
    };
}

export const toPoolLite = (pool: InventoryPool): PoolLite => ({ id: pool.id, name: pool.name, active: pool.active });

/** "price x 1.10, at least 0.02": the rule's pricing in the words the billing pages use. */
export function summarizeBillingRule(rule: BillingRule): string {
    const pricing =
        rule.multiplier !== null
            ? `price x ${rule.multiplier}`
            : rule.flatMarkup !== null
              ? `price + ${rule.flatMarkup}`
              : 'price unchanged';
    return rule.minPrice !== null ? `${pricing}, at least ${rule.minPrice}` : pricing;
}

export const toBillingRuleLite = (rule: BillingRule): BillingRuleLite => ({
    id: rule.id,
    ownerFirmId: rule.firmId,
    targetFirmId: rule.childFirmId,
    summary: summarizeBillingRule(rule),
    enabled: rule.enabled,
    routeId: rule.routeId ?? undefined,
    providerId: rule.providerId ?? undefined,
});
